import React from 'react';
import type { Language } from '../types';
import { getTranslation } from '../utils/translations';

type InfoNoticeVariant = 'success' | 'info' | 'error';

interface InfoNoticeModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    message?: string;
    variant?: InfoNoticeVariant;
    language?: Language;
    actionLabel?: string;
    onAction?: () => void;
}

const variantStyles: Record<InfoNoticeVariant, { iconBg: string; iconColor: string }> = {
    success: {
        iconBg: 'bg-emerald-100 dark:bg-emerald-900/30',
        iconColor: 'text-emerald-600 dark:text-emerald-400',
    },
    info: {
        iconBg: 'bg-sky-100 dark:bg-sky-900/30',
        iconColor: 'text-sky-600 dark:text-sky-400',
    },
    error: {
        iconBg: 'bg-red-100 dark:bg-red-900/30',
        iconColor: 'text-red-600 dark:text-red-400',
    },
};

const InfoNoticeModal: React.FC<InfoNoticeModalProps> = ({
    isOpen,
    onClose,
    title,
    message,
    variant = 'info',
    language,
    actionLabel,
    onAction,
}) => {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(language, key);
    const styles = variantStyles[variant];

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[650] p-4 animate-fade-in"
            onClick={onClose}
        >
            <div
                className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-xs overflow-hidden border border-white/20 dark:border-slate-800"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-6 text-center">
                    <div className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full mb-4 ${styles.iconBg} ${styles.iconColor}`}>
                        {variant === 'success' ? (
                            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                            </svg>
                        ) : variant === 'error' ? (
                            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        ) : (
                            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        )}
                    </div>
                    <h3 className="text-lg font-black text-slate-800 dark:text-slate-100">{title}</h3>
                    {message && (
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-2 leading-relaxed whitespace-pre-wrap break-all">{message}</p>
                    )}
                </div>
                <div className="bg-slate-50 dark:bg-slate-800 p-4">
                    <div className={`flex gap-2 ${actionLabel && onAction ? '' : ''}`}>
                        {actionLabel && onAction && (
                            <button
                                type="button"
                                onClick={() => {
                                    onAction();
                                    onClose();
                                }}
                                className="flex-1 px-4 py-2.5 bg-brand-sage text-white font-bold rounded-xl hover:opacity-90 transition-all text-sm"
                            >
                                {actionLabel}
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={onClose}
                            className={`${actionLabel && onAction ? 'flex-1' : 'w-full'} px-4 py-2.5 bg-slate-900 dark:bg-slate-700 text-white font-bold rounded-xl hover:bg-slate-800 dark:hover:bg-slate-600 transition-all text-sm`}
                        >
                            {t('ok')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InfoNoticeModal;
